import { Component, OnDestroy, OnInit, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { ContentfulService } from '../services/contentful.service';
import { LoggingService } from '../services/logging.service';
import { SearchEngineService } from '../services/search-engine.service';
import { Subscription, filter } from 'rxjs';
import { UserService } from '../services/user.service';
import { ApiService } from '../core/http/api.service';
import { InvestorDashboard, UsdBalanceSummary, InvestorDashboardPortfolioSeriesBalanceSummary, InvestorDetails, PortfolioHierarchy } from '../models/classes/investor-dashboard.class';
import { TourMatMenu, TourService } from 'ngx-ui-tour-md-menu';
import { EnvironmentVariablesService } from '../services/environment-variables.service';
import { NgTourStepsService } from '../services/ng-tour-steps.service';
import { BreadcrumbDataService } from '../services/breadcrumb-data.service';
import { InvestorMeta } from '../models/classes/investor-meta.class';
import { CommonModule } from '@angular/common';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { NoAccessErrorComponent } from '../shared/no-access-error/no-access-error.component';
import { FormsModule } from '@angular/forms';
import { InvestorSummaryComponent } from './investor-summary/investor-summary.component';
import { InvestorPortfolioSummaryComponent } from './investor-portfolio-summary/investor-portfolio-summary.component';
import { GcmDatePickerComponent, GcmLoadingService, GcmMatDialogService, GcmNotificationService} from '@gcm/gcm-ui-angular';
import { InvestorsStorageService } from '../services/investors-storage.service';
import { ErrorMessageService } from '../services/error-message.service';

@Component({
  selector: 'app-investor-dashboard',
  imports: [
    CommonModule,
    DateFormatPipe,
    NoAccessErrorComponent,
    FormsModule,
    InvestorSummaryComponent,
    InvestorPortfolioSummaryComponent,
    GcmDatePickerComponent,
    RouterModule,
    TourMatMenu
  ],
  templateUrl: './investor-dashboard.component.html',
  styleUrls: ['./investor-dashboard.component.scss']
})
export class InvestorDashboardComponent implements OnInit, OnDestroy {
  private contentfulService = inject(ContentfulService);
  private seoService = inject(SearchEngineService);
  private router = inject(Router);
  private userService = inject(UserService);
  private loggingService = inject(LoggingService);
  private notificationService = inject(GcmNotificationService);
  private apiService = inject(ApiService);
  private loadingService = inject(GcmLoadingService);
  private ngTourSteps = inject(NgTourStepsService);
  private tourService = inject(TourService);
  private evService = inject(EnvironmentVariablesService);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private dateFormat = inject(DateFormatPipe);
  private dialogService = inject(GcmMatDialogService);
  private investorsStorage = inject(InvestorsStorageService);
  private errorMessageService = inject(ErrorMessageService);

  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: null as unknown as Record<string, string> }
  );
  errorMessage!: string;
  currentPage!: string;
  investorSummary!: InvestorDetails;
  HeadingasOfDate = '';
  investorPortfolioData = signal<PortfolioHierarchy[]>([]);
  readonly activeInvestor = toSignal(this.investorsStorage.getActiveInvestor(), { initialValue: null as unknown as InvestorMeta });
  private readonly loadOnActiveInvestor = effect(() => {
    const res = this.activeInvestor();
    if (!res) return;
    this.activeInvestorMetadata = res;
    this.getAsOfDates();
    if (res.id) {
      this.refreshActiveInvestorData({ id: res.id });
    }
  });
  reportingPeriod = signal<string>('');
  readonly investorId = input<number | undefined>(undefined);
  readonly reportingdate = input<string | undefined>(undefined);

  private readonly syncReportingFromDate = effect(() => {
    const value = this.reportingdate();
    if (value) {
      const formatted = this.dateFormat.transform(value, "MM/dd/yyyy") ?? '';
      this.reportingPeriod.set(formatted);
      this.selectedAsOfDate.set(formatted);
    }
  });

  showNoAccessError = false;
  tourStart$!: Subscription;
  activeInvestorMetadata!: InvestorMeta;
  selectedAsOfDate = signal<string>('');
  asOfDropdownData: string[] = [];
  showBanner = false;

  async ngOnInit(): Promise<void> {
    this.showNoAccessError = false;
    this.seoService.setCanonicalURL();
    //// Get contentFul common Lables ////
    this.currentPage = this.router.url?.substring(1).split('/')[0];
    this.initializeUiTourForInvestorDashboard();
    this.userService.deleteCookieValue('dashboardPortfolioId');
  }

  getAsOfDates() {
    this.asOfDropdownData = [];
    const asOfDateData = this.activeInvestorMetadata.reportingPeriods
    const hidePriorToDate = new Date(this.evService.hideDatesPriorToDate)
    let formattedDateData: (string | null)[] = [];
    asOfDateData.forEach(date => {
      formattedDateData.push(this.dateFormat.transform(date, "MM/dd/yyyy"))
    })
    formattedDateData = formattedDateData.filter(d => d && new Date(d).getTime() >= hidePriorToDate.getTime()) //Remove dates prior to cutoff
    this.asOfDropdownData = (formattedDateData as string[]).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    const currentReportingPeriod = this.reportingPeriod();
    if (currentReportingPeriod && this.asOfDropdownData.find(date => date === currentReportingPeriod)) {
      this.selectedAsOfDate.set(currentReportingPeriod);
    } else {
      this.selectedAsOfDate.set(this.asOfDropdownData[0]);
    }
  }

  checkInvestorAccess(investorId: number) {
    const assignedInvestorIds = this.userService.getInvestorIds();
    if (this.userService.isExternalUser()) {
      if (investorId && !assignedInvestorIds.includes(parseInt(investorId.toString()))) {
        const parametersErrorMessage = "Do not have Access to the Investor " + investorId;
        const error = "On " + this.currentPage.toUpperCase() + " - " + parametersErrorMessage;
        this.showNoAccessError = true;
        this.loggingService.log(this.userService.getUserEmail() + ' - ' + error);
        return;
      }
    }
  }

  refreshActiveInvestorData(requestObj: { id: number, requestedReportingPeriod?: string }): void {
    this.showNoAccessError = false;
    this.errorMessage = '';
    this.investorPortfolioData.set([]);
    const reportingPeriod = requestObj.requestedReportingPeriod ?? this.breadcrumbDataService.getReportingPeriodForSelectedInvestor(requestObj.id);
    this.fetchDashboardDataFromApi(requestObj.id, reportingPeriod).then((rawData) => {
      const data = rawData as InvestorDashboard;
      this.investorSummary = data.summary;
      this.router.navigate(
        ['/investor/' + this.investorSummary.masterId], {
        queryParams: { reportingdate: reportingPeriod },
        queryParamsHandling: 'merge'
      });
      const portfolios = this.convertPortfolios(data?.portfolioBalanceSummaries)
      const portfolioSeries = this.convertPortfolioSeries(data?.portfolioSeriesBalanceSummaries);
      this.investorPortfolioData.set(data?.portfolios);
      this.investorPortfolioData().forEach(portfolioData => {
        portfolioData.portfolioBalanceSummary = portfolios.filter(p => p.masterId === portfolioData.masterId).map(p => InvestorDashboardPortfolioSeriesBalanceSummary.fromUsdBalanceSummary(p));
        portfolioData.portfolioSeries.forEach(series => {
          series.portfolioSeriesBalanceSummary = portfolioSeries.filter(ps => ps.masterId === series.masterId);
        })
      })
      this.showBanner = this.investorPortfolioData().filter(x => x.reportingPeriod.slice(0, 10) != this.dateFormat.transform(this.selectedAsOfDate(), "yyyy-MM-dd")).length > 0;
    }, err => {
      this.loggingService.log("----- Dashboard investor API Exception -----", err);
      this.notificationService.notifyError(err);
    })
  }

  ngOnDestroy(): void {
    this.tourStart$.unsubscribe();
  }

  fetchDashboardDataFromApi(investorId: number, reportingDate: string) {
    const loadingTask = 'fetchDashboardDataFromApi';
    this.loadingService.addAsyncTask(loadingTask);
    return new Promise((resolve, reject) => {
      this.apiService.getInvestorPortfolioDashboardDetails(Number(investorId), reportingDate).subscribe({
        next: (data) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          resolve(data);
        },
        error: (err) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.errorMessage = this.errorMessageService.getErrorMessage(err);
          reject(err);
        }
      });
    })
  }

  updateInvestor(id: number) {
    this.refreshActiveInvestorData({ id })
  }

  initializeUiTourForInvestorDashboard() {
    this.ngTourSteps.getContentfulDataFetchedValue().subscribe((data: boolean) => {
      if (data) this.tourService.initialize(this.ngTourSteps.investorDashboardSteps, this.ngTourSteps.defaultOptions);
    })
    this.tourStart$ = this.tourService.start$.subscribe(() => {
      this.tourService.end$.subscribe(() => {
        this.dialogService.showInformationDialog('Tour Completed', `Click <img src="./../${this.evService.assetsFolderPath}/images/tour.png" class="mx-1"> at anytime if you wish to return to tour`, 'OK', 'tour-completed-dialog');
        this.tourService.end$.complete();
        this.tourService.start$.complete();
      })
    })

    this.tourService.stepHide$.subscribe(data => {
      if (data.direction === 0 && data.step.anchorId === "investor.dashboard.portfolio") {
        (document.querySelectorAll('[data-cy="expandIcon00"]')[0] as HTMLElement)?.click();
      }
    })
  }

  convertPortfolios(input: Record<number,Record<string,UsdBalanceSummary>>): UsdBalanceSummary[] {
    const result: UsdBalanceSummary[] = [];
    for (const [portfolioId, value] of Object.entries(input)) {
      for (const [reportingPeriod, subValue] of Object.entries(value)) {
        subValue.masterId = Number(portfolioId);
        subValue.reportingPeriod = reportingPeriod;
        result.push(subValue);
      }
    }
    return result;
  }

  convertPortfolioSeries(input: Record<number,Record<string,InvestorDashboardPortfolioSeriesBalanceSummary>>): InvestorDashboardPortfolioSeriesBalanceSummary[] {
    const result: InvestorDashboardPortfolioSeriesBalanceSummary[] = [];
    for (const [portfolioSeriesId, value] of Object.entries(input)) {
      for (const [reportingPeriod, subValue] of Object.entries(value)) {
        subValue.masterId = Number(portfolioSeriesId);
        subValue.reportingPeriod = reportingPeriod;
        result.push(subValue);
      }
    }
    return result;
  }

  updateAsOfDate(date: string | string[] | Date | Date[] | (string | Date)[] | { start: string | Date; end: string | Date; type: string } | undefined) {
    if (!date || typeof date !== 'string') return;
    const formattedDate = this.dateFormat.transform(date, 'yyyy-MM-dd');
    if (date) {
      const reportingDate = (formattedDate as string).slice(0, 10);
      this.reportingPeriod.set(reportingDate);
      this.router.navigate(
        [], {
        queryParams: { reportingdate: reportingDate },
        queryParamsHandling: 'merge'
      });
      this.refreshActiveInvestorData({ id: this.investorId() as number, requestedReportingPeriod: reportingDate })
    }
  }
}
